/**
 * Bow styles + the inner-ten default rule — the frontend's single source
 * (issue #2048).
 *
 * The option list comes from `shared/enums.json`, the same file the backend
 * reads (app/shared_enums.py) and the same convention as
 * `shared/face-definitions.json`. Adding a bow style is a one-place edit
 * there plus the matching member in `backend/app/models.py::BowStyle`;
 * `backend/tests/test_enum_parity.py` fails until the two agree.
 *
 * This module is the ONLY place in `src/` allowed to spell out the list or the
 * rule `inner_ten` defaults on for compound. `src/lib/bowStyleParity.test.js`
 * scans the source tree and fails if a copy reappears.
 */
import enums from "../../../shared/enums.json";

/** Ordered `{ value, label }` options — render these directly. */
export const BOW_STYLE_OPTIONS = enums.bow_style.values;

/** Ordered enum values, no labels. */
export const BOW_STYLES = BOW_STYLE_OPTIONS.map((o) => o.value);

/** value → display label. */
export const BOW_STYLE_LABEL = Object.fromEntries(
  BOW_STYLE_OPTIONS.map((o) => [o.value, o.label]),
);

/**
 * The one bow style that carries the inner-ten default.
 *
 * Exported so callers never have to write the literal; compare through
 * `defaultInnerTen()` rather than against this where you can.
 */
export const INNER_TEN_BOW_STYLE = enums.bow_style.inner_ten_default;

/**
 * The archery rule: compound archers shoot the inner-10 ("only the X-ring
 * scores 10") variant by default on WA ten-ring faces, so choosing that bow
 * style pre-sets the per-archer `inner_ten` flag.
 *
 * A DEFAULT, not a constraint — the archer can always toggle the inner-ring
 * checkbox afterwards, and `resolveInnerTen` lets a stored preference win.
 *
 * @param {string|null|undefined} bowStyle
 * @returns {boolean}
 */
export function defaultInnerTen(bowStyle) {
  return bowStyle === INNER_TEN_BOW_STYLE;
}

/**
 * `inner_ten` for an archer whose stored preference may or may not be set:
 * an explicit stored boolean wins, otherwise fall back to the bow-style
 * default. Null/undefined `preferred` means "never chosen", not "off".
 *
 * @param {boolean|null|undefined} preferred stored preferred_inner_ten
 * @param {string|null|undefined} bowStyle
 * @returns {boolean}
 */
export function resolveInnerTen(preferred, bowStyle) {
  return preferred ?? defaultInnerTen(bowStyle);
}
